/**
 * Convert heading text to URL-safe slug
 * Example: "Introduction to AI" -> "introduction-to-ai"
 */
function generateSlug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * Create a unique anchor ID for a heading
 * Format: {messageId}-{slug} with numeric suffix for duplicates
 * Example: "message_abc123-introduction" or "message_abc123-summary-2"
 */
export function createAnchorId(messageId: string, headingText: string, existingIds: Set<string>): string {
  const slug = generateSlug(headingText);
  const baseId = `${messageId}-${slug}`;

  // Handle duplicates by adding numeric suffix
  let anchorId = baseId;
  let counter = 2;
  while (existingIds.has(anchorId)) {
    anchorId = `${baseId}-${counter}`;
    counter++;
  }

  return anchorId;
}

/**
 * Collect the answer-section wrapper(s) that belong to a heading element.
 * For H1 headings this includes every following subsection until the next H1.
 */
function getSectionTargets(element: HTMLElement): HTMLElement[] {
  const sectionWrapper = element.closest("[data-answer-section]") as HTMLElement | null;
  if (!sectionWrapper) return [element];

  const targets: HTMLElement[] = [sectionWrapper];
  if (element.tagName === "H1" && sectionWrapper.getAttribute("data-heading-level") === "1") {
    let next: Element | null = sectionWrapper.nextElementSibling;
    while (next) {
      if (next.hasAttribute("data-answer-section")) {
        if (next.getAttribute("data-heading-level") === "1") break;
        targets.push(next as HTMLElement);
      }
      next = next.nextElementSibling;
    }
  }
  return targets;
}

/** Resolve the nearest enclosing section heading (h1-h3) for a hovered element, within the content root. */
export function getHeadingAncestor(el: HTMLElement | null, root: HTMLElement | null): HTMLElement | null {
  if (!el || !root) return null;
  const heading = el.closest<HTMLElement>("h1, h2, h3");
  return heading && root.contains(heading) ? heading : null;
}

/**
 * Text spanning a section: the heading plus its body up to the next heading of
 * the same or higher level. A share anchor keyed on the heading text alone lands
 * on the first identical text anywhere in the thread — a generic heading like
 * "Summary" resolves to the wrong place. Spanning the section gives distinct
 * start/end anchors that pin the match to this section and highlight its body.
 */
export function getSectionShareText(heading: HTMLElement): string {
  const level = Number(heading.tagName.slice(1)) || 6;
  const parts = [heading.textContent ?? ""];
  let next: Element | null = heading.nextElementSibling;
  while (next) {
    const tag = next.tagName;
    if (/^H[1-6]$/.test(tag) && Number(tag.slice(1)) <= level) break;
    parts.push(next.textContent ?? "");
    next = next.nextElementSibling;
  }
  return parts.join(" ").replace(/\s+/g, " ").trim();
}

/** A heading of an answer, with the section text a share link anchors on. */
export interface OutlineHeading {
  text: string;
  level: 1 | 2 | 3;
  sectionText: string;
}

const HEADING_LABEL_CHARS = 120;

/**
 * Headings of the last answer rendered in the thread, in document order, empty when that answer
 * has none. The share modal renders outside the TOC provider, so its outline comes from the DOM
 * rather than the registered headings.
 *
 * `skipMessageId` names the message being streamed, whose headings are a prefix of the finished
 * ones. It is matched by id rather than by position: a turn that has started streaming but not yet
 * painted its first frame would otherwise cost the caller the finished answer below it.
 */
export function getLastAnswerOutline(skipMessageId?: string): OutlineHeading[] {
  const messages = Array.from(document.querySelectorAll<HTMLElement>("[data-message-renderer]"));

  for (const message of messages.toReversed()) {
    if (skipMessageId && message.getAttribute("data-message-renderer") === skipMessageId) continue;
    // Only anchored headings carry an id, and only those can be linked to.
    const headings = Array.from(message.querySelectorAll<HTMLElement>("h1, h2, h3")).filter((h) => h.id);
    if (headings.length === 0) continue;
    return headings
      .map((heading) => ({
        text: (heading.textContent ?? "").trim().replace(/\s+/g, " ").slice(0, HEADING_LABEL_CHARS),
        level: Number(heading.tagName.slice(1)) as 1 | 2 | 3,
        sectionText: getSectionShareText(heading),
      }))
      .filter((entry) => entry.text && entry.sectionText);
  }
  return [];
}

/** X offset from content left to sample for element under cursor (in content area, not on the line) */
const CONTENT_SAMPLE_OFFSET_PX = 80;

/**
 * Return the block element(s) to highlight at a given point (e.g. under the dotted line).
 * Uses elementFromPoint in the content area so we get: whole section for headings, single p, or single li.
 */
export function getBlockHighlightTargetsAtPoint(
  _clientX: number,
  clientY: number,
  contentRootId: string
): HTMLElement[] {
  const contentRoot = document.getElementById(contentRootId);
  if (!contentRoot) return [];

  const contentRect = contentRoot.getBoundingClientRect();
  const sampleX = contentRect.left + Math.min(CONTENT_SAMPLE_OFFSET_PX, contentRect.width / 2);
  const el = document.elementFromPoint(sampleX, clientY) as HTMLElement | null;
  if (!el || !contentRoot.contains(el)) return [contentRoot];

  const heading = el.closest("h1, h2, h3, h4, h5, h6") as HTMLElement | null;
  if (heading && contentRoot.contains(heading)) return getSectionTargets(heading);

  const p = el.closest("p");
  if (p && contentRoot.contains(p)) return [p];

  const li = el.closest("li");
  if (li && contentRoot.contains(li)) return [li];

  const section = el.closest("[data-answer-section]") as HTMLElement | null;
  if (section && contentRoot.contains(section)) return [section];

  return [contentRoot];
}

/**
 * Return block element(s) to highlight for a given element (e.g. text node parent from scrollToText).
 * Same block types as getBlockHighlightTargetsAtPoint: section for headings, single p, or single li.
 */
export function getBlockHighlightTargetsForElement(element: HTMLElement, contentRootId: string): HTMLElement[] {
  const contentRoot = document.getElementById(contentRootId);
  if (!contentRoot || !contentRoot.contains(element)) return [];

  const heading = element.closest("h1, h2, h3, h4, h5, h6") as HTMLElement | null;
  if (heading && contentRoot.contains(heading)) return getSectionTargets(heading);

  const p = element.closest("p");
  if (p && contentRoot.contains(p)) return [p];

  const li = element.closest("li");
  if (li && contentRoot.contains(li)) return [li];

  const section = element.closest("[data-answer-section]") as HTMLElement | null;
  if (section && contentRoot.contains(section)) return [section];

  return [contentRoot];
}

/**
 * Return block elements that overlap a vertical range (for drag-to-select on the dotted line).
 * Uses same block types as getBlockHighlightTargetsAtPoint; returns them in DOM order.
 */
export function getBlockHighlightTargetsInRange(
  containerRect: DOMRect,
  startY: number,
  endY: number,
  contentRootId: string
): HTMLElement[] {
  const contentRoot = document.getElementById(contentRootId);
  if (!contentRoot) return [];

  const startClientY = containerRect.top + Math.min(startY, endY);
  const endClientY = containerRect.top + Math.max(startY, endY);

  const candidates = contentRoot.querySelectorAll<HTMLElement>("[data-answer-section], p, li");
  const overlapping: HTMLElement[] = [];
  candidates.forEach((el) => {
    const rect = el.getBoundingClientRect();
    if (rect.bottom >= startClientY && rect.top <= endClientY) overlapping.push(el);
  });

  const topLevel = overlapping.filter((el) => !overlapping.some((other) => other !== el && other.contains(el)));
  topLevel.sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
  return topLevel.length > 0 ? topLevel : [contentRoot];
}

const HEADING_ANCHOR_HIGHLIGHT_CLASS = "heading-anchor-highlight";
/** Dotted-line hover: no padding/margin (no layout shift), no animation, 50% fill */
const HEADING_ANCHOR_HIGHLIGHT_HOVER_CLASS = "heading-anchor-highlight-hover";

/** Centres the element in its scroll container, or in the viewport when no container is given. */
function scrollElementIntoView(element: HTMLElement, scrollContainer: HTMLElement | null): void {
  if (scrollContainer) {
    const containerRect = scrollContainer.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    const elementTopInContent = scrollContainer.scrollTop + (elementRect.top - containerRect.top);
    const targetScrollTop = elementTopInContent - scrollContainer.clientHeight / 2 + element.offsetHeight / 2;
    const clamped = Math.max(0, Math.min(targetScrollTop, scrollContainer.scrollHeight - scrollContainer.clientHeight));
    scrollContainer.scrollTo({ top: clamped, behavior: "smooth" });
  } else {
    element.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

/**
 * Scroll to a heading with the given anchor ID
 * Optionally highlights the section for a few seconds
 * @param scrollContainer - Element to scroll instead of window
 */
export function scrollToAnchor(anchorId: string, highlight = true, scrollContainer: HTMLElement | null = null): void {
  const element = document.getElementById(anchorId);
  if (!element) {
    console.warn(`Element with id "${anchorId}" not found`);
    return;
  }

  scrollElementIntoView(element, scrollContainer);

  if (highlight) {
    const highlightTargets = getSectionTargets(element);
    highlightTargets.forEach((el) => el.classList.add(HEADING_ANCHOR_HIGHLIGHT_CLASS));
    const HIGHLIGHT_DURATION_MS = 4500;
    setTimeout(() => {
      highlightTargets.forEach((el) => el.classList.remove(HEADING_ANCHOR_HIGHLIGHT_CLASS));
    }, HIGHLIGHT_DURATION_MS);
  }
}

/**
 * Scroll to a whole message, reporting whether it was there to scroll to. Only headings carry an
 * element id (the one `createAnchorId` mints), so a message is located the way every other message
 * lookup in the app locates one: by the id its renderer puts on `data-message-renderer`. Matching
 * the attribute by walk rather than by selector keeps ids out of CSS selectors, where they would
 * need escaping. No highlight: the section tint pads and animates what it lands on, which on a
 * whole message would shift the page for its duration.
 *
 * A `false` return is not an error — the chat list is virtualized, so a message far from the
 * current scroll position is simply not mounted. The caller decides what to do about that.
 */
export function scrollToMessage(messageId: string, scrollContainer: HTMLElement | null = null): boolean {
  let element: HTMLElement | null = null;
  for (const candidate of document.querySelectorAll("[data-message-renderer]")) {
    if (candidate.getAttribute("data-message-renderer") === messageId) {
      element = candidate as HTMLElement;
      break;
    }
  }
  if (!element) return false;

  scrollElementIntoView(element, scrollContainer);
  return true;
}

/** Select section(s) matching the anchor, same scope as scrollToAnchor highlight */
export function selectSectionInDocument(anchorId: string): boolean {
  const element = document.getElementById(anchorId);
  if (!element) return false;

  const targets = getSectionTargets(element);
  if (targets.length === 0) return false;

  try {
    const sel = window.getSelection();
    if (!sel) return false;
    const range = document.createRange();
    const first = targets[0];
    const last = targets[targets.length - 1];
    range.setStartBefore(first);
    range.setEndAfter(last);
    sel.removeAllRanges();
    sel.addRange(range);
    return true;
  } catch {
    return false;
  }
}

let currentBlockHighlightElements: HTMLElement[] = [];

function getBlockHighlightTargets(anchorId: string, blockIdForWholeAnswer?: string): HTMLElement[] {
  if (anchorId) {
    const element = document.getElementById(anchorId);
    if (!element) return [];
    return getSectionTargets(element);
  }
  if (blockIdForWholeAnswer) {
    const el = document.getElementById(blockIdForWholeAnswer);
    return el ? [el] : [];
  }
  return [];
}

/**
 * Apply or clear the block highlight for dotted-line hover. No layout shift, no timer.
 * Uses a background-only class (50% fill) that stays until cleared.
 */
export function setSectionBlockHighlight(anchorId: string | null, blockIdForWholeAnswer?: string): void {
  const isClear = anchorId === null && !blockIdForWholeAnswer;
  if (isClear) {
    currentBlockHighlightElements.forEach((el) => el.classList.remove(HEADING_ANCHOR_HIGHLIGHT_HOVER_CLASS));
    currentBlockHighlightElements = [];
    return;
  }
  currentBlockHighlightElements.forEach((el) => el.classList.remove(HEADING_ANCHOR_HIGHLIGHT_HOVER_CLASS));
  currentBlockHighlightElements = [];
  if (anchorId !== null || blockIdForWholeAnswer) {
    currentBlockHighlightElements = getBlockHighlightTargets(anchorId ?? "", blockIdForWholeAnswer);
    currentBlockHighlightElements.forEach((el) => el.classList.add(HEADING_ANCHOR_HIGHLIGHT_HOVER_CLASS));
  }
}

/**
 * Apply block highlight to specific elements (for dotted-line hover at a point).
 * Replaces any existing hover highlight.
 */
export function setSectionBlockHighlightByElements(elements: HTMLElement[]): void {
  const isClear = elements.length === 0;
  if (isClear) {
    currentBlockHighlightElements.forEach((el) => el.classList.remove(HEADING_ANCHOR_HIGHLIGHT_HOVER_CLASS));
    currentBlockHighlightElements = [];
    return;
  }
  currentBlockHighlightElements.forEach((el) => el.classList.remove(HEADING_ANCHOR_HIGHLIGHT_HOVER_CLASS));
  currentBlockHighlightElements = elements;
  currentBlockHighlightElements.forEach((el) => el.classList.add(HEADING_ANCHOR_HIGHLIGHT_HOVER_CLASS));
}

/**
 * Select the given elements in the document (for "Do more with this text" on bolt click).
 */
export function selectElementsInDocument(elements: HTMLElement[]): boolean {
  if (elements.length === 0) return false;
  try {
    const sel = window.getSelection();
    if (!sel) return false;
    const range = document.createRange();
    const first = elements[0];
    const last = elements[elements.length - 1];
    range.setStartBefore(first);
    range.setEndAfter(last);
    sel.removeAllRanges();
    sel.addRange(range);
    return true;
  } catch {
    return false;
  }
}

/**
 * Set the document selection to the text range between two Y positions (line-by-line drag selection).
 * Uses caretPositionFromPoint so the selection follows lines rather than jumping by block.
 */
export function setSelectionFromYRange(
  containerRect: DOMRect,
  startY: number,
  endY: number,
  contentRootId: string
): boolean {
  const contentRoot = document.getElementById(contentRootId);
  if (!contentRoot) return false;
  const contentRect = contentRoot.getBoundingClientRect();
  const sampleX = contentRect.left + Math.min(CONTENT_SAMPLE_OFFSET_PX, contentRect.width / 2);
  const topClientY = containerRect.top + Math.min(startY, endY);
  const bottomClientY = containerRect.top + Math.max(startY, endY);

  const doc = contentRoot.ownerDocument;
  // Older Safari releases lack it.
  if (typeof doc.caretPositionFromPoint !== "function") return false;

  const positionAtTop = doc.caretPositionFromPoint(sampleX, topClientY);
  const positionAtBottom = doc.caretPositionFromPoint(sampleX, bottomClientY);
  if (!positionAtTop || !positionAtBottom) return false;

  try {
    const sel = window.getSelection();
    if (!sel) return false;
    const range = document.createRange();
    range.setStart(positionAtTop.offsetNode, positionAtTop.offset);
    range.setEnd(positionAtBottom.offsetNode, positionAtBottom.offset);
    sel.removeAllRanges();
    sel.addRange(range);
    return true;
  } catch {
    return false;
  }
}
